import { Spin } from "antd"
import moment from "moment"
import { useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import EmptyData from "@/components/common/EmptyData"
import { RibbonCard } from "@/components/ui/ribbon-card"
import { VariantProps } from "class-variance-authority"
import { IPaginationMeta, ISapReservation } from "@/types"

interface Props {
  reservations?: ISapReservation[]
  isLoading?: boolean
  handleReset?: () => void
  onLoadMore?: () => void
  meta?: IPaginationMeta
  hasMore?: boolean
}

const ReservationCard = ({ reservations, isLoading, handleReset, onLoadMore, hasMore = true }: Props) => {
  const observerTarget = useRef<HTMLDivElement | null>(null)
  const loadingRef = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !loadingRef.current) {
          loadingRef.current = true
          onLoadMore?.()
        }
      },
      { threshold: 0.1 }
    )

    const target = observerTarget.current

    if (target) {
      observer.observe(target)
    }

    return () => {
      if (target) {
        observer.unobserve(target)
      }
    }
  }, [hasMore, isLoading, onLoadMore])

  useEffect(() => {
    if (!isLoading) {
      loadingRef.current = false
    }
  }, [isLoading])

  if (reservations?.length === 0 && !isLoading) {
    return <EmptyData handleReset={handleReset} />
  }

  const renderStatus = (status: string): VariantProps<typeof RibbonCard>["variant"] => {
    switch (status) {
      case "Waiting Approval":
        return "warning"
      case "Approved":
        return "success"
      case "Reject":
      case "Cancel":
        return "error"
      default:
        return "black"
    }
  }

  return (
    <Spin spinning={isLoading} style={{ marginTop: 16 }}>
      <div className="md:space-y-6 space-y-4">
        {reservations?.map(reservation => (
          <RibbonCard key={reservation.record_id} position="bottom-right" variant={renderStatus(reservation.approval_status || "")} ribbon={reservation.approval_status}>
            <div className="flex flex-col justify-between md:gap-3 gap-2" onClick={() => navigate(`/saprsv/view/${reservation.record_id}`)}>
              <div className="flex flex-col">
                <p className="md:text-sm text-xs font-semibold">{reservation.fullname}</p>
                <span className="md:text-xs text-[10px] font-medium">Record ID: {reservation.record_id}</span>
                {reservation.description ? <span className="md:text-xs text-[10px] text-gray-500 mt-1">{reservation.description}</span> : null}
              </div>
              <span className="text-xs text-gray-500">{moment(reservation.updated_at).format("LLL")}</span>
            </div>
          </RibbonCard>
        ))}
      </div>

      <div ref={observerTarget} style={{ height: 20, marginTop: 16 }} />

      {isLoading && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin />
        </div>
      )}

      {!hasMore && reservations && reservations.length > 0 && <div style={{ textAlign: "center", padding: "20px 0", color: "#8c8c8c" }}>No more items to load</div>}
    </Spin>
  )
}

export default ReservationCard
